import React, { useCallback, useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import RecordApproval from "@/components/RecordApproval";
import { staffTitles } from "@/lib/staffTitles";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { StudentPicker } from "@/components/RecordSection";
import { gradesForSchool, normalizeGrade } from "@/lib/sectionConfigs";
import CouncilRecordPrint, { useCouncilPrint } from "@/components/CouncilRecordPrint";
import { MultiChoice, MeetingSelect, MEETING_TIMES, MEETING_LOCATIONS, MEETING_STATUSES } from "@/components/MeetingChoices";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CalendarDays, Check, ClipboardList, Clock3, Crown, GraduationCap, Pencil, Plus, Users, Trash2, Printer,
} from "lucide-react";

const ROSTER_TYPE = "student_council_roster";
const MEETING_TYPE = "student_council_meeting";
const TOPICS = [
  "الانضباط المدرسي",
  "الغياب والتأخر",
  "السلوك الإيجابي",
  "النظافة والمحافظة على المرافق",
  "المبادرات الطلابية",
  "الأنشطة والبرامج المدرسية",
  "البيئة المدرسية",
  "التواصل والتعاون بين الطلاب",
  "مقترحات الطلاب",
];
const DECISIONS = [
  "تنفيذ مبادرة طلابية",
  "إطلاق حملة توعوية",
  "رفع المقترح لإدارة المدرسة",
  "التنسيق مع رائد النشاط",
  "متابعة الموضوع في الاجتماع القادم",
  "معالجة الموضوع بالتعاون مع الطلاب",
  "تمت المعالجة",
];

function formatDate(date) {
  if (!date) return "دون تاريخ";
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? date
    : parsed.toLocaleDateString("ar-SA", { year: "numeric", month: "long", day: "numeric" });
}

function MeetingDialog({ open, onOpenChange, meeting, members, onSave }) {
  const [form, setForm] = useState({
    date: "",
    time: "",
    location: "",
    topics: [],
    decisions: [],
    attendeeIds: [],
    status: "scheduled",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(meeting ? {
      date: meeting.data.date || "",
      time: meeting.data.time || "",
      location: meeting.data.location || "",
      topics: meeting.data.topics || [],
      decisions: meeting.data.decisions || [],
      attendeeIds: meeting.data.attendee_ids || meeting.data.attendeeIds || meeting.data.attendees?.map((attendee) => attendee.student_id) || [],
      status: meeting.data.status || "scheduled",
    } : {
      date: "",
      time: "",
      location: "",
      topics: [],
      decisions: [],
      attendeeIds: [],
      status: "scheduled",
    });
  }, [meeting, open]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const { attendeeIds, ...fields } = form;
      await onSave({
        ...fields,
        attendee_ids: attendeeIds,
        attendees: members
          .filter((student) => form.attendeeIds.includes(student.student_id))
          .map((student) => ({
            student_id: student.student_id,
            name: student.student_name,
            grade: student.grade,
          })),
      }, meeting?.id);
      onOpenChange(false);
    } catch {
      // Keep the dialog open after a failed save so the user can retry.
    } finally {
      setSaving(false);
    }
  };

  const optionsFor = (values) => values.map((item) => ({ value: item, label: item }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{meeting ? "تعديل اجتماع المجلس" : "إضافة اجتماع للمجلس"}</DialogTitle>
          <DialogDescription>اختر بيانات الاجتماع ومحاوره والحضور والقرارات من القوائم.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="student-council-date">تاريخ الاجتماع</Label>
              <input id="student-council-date" type="date" value={form.date} onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))} required className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" />
            </div>
            <MeetingSelect label="وقت الاجتماع" value={form.time} options={MEETING_TIMES} onChange={(time) => setForm((current) => ({ ...current, time }))} />
            <MeetingSelect label="مكان الاجتماع" value={form.location} options={MEETING_LOCATIONS} onChange={(location) => setForm((current) => ({ ...current, location }))} />
            <div className="space-y-2">
              <Label>حالة الاجتماع</Label>
              <Select value={form.status} onValueChange={(status) => setForm((current) => ({ ...current, status }))}>
                <SelectTrigger aria-label="حالة الاجتماع"><SelectValue /></SelectTrigger>
                <SelectContent>{MEETING_STATUSES.map((status) => <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <MultiChoice label="موضوعات الاجتماع" options={optionsFor(TOPICS)} value={form.topics} onChange={(topics) => setForm((current) => ({ ...current, topics }))} />
          <MultiChoice
            label="أعضاء المجلس الحاضرون"
            options={members.map((student) => ({ value: student.student_id, label: `${student.student_name} — ${student.grade || "دون صف"}` }))}
            value={form.attendeeIds}
            onChange={(attendeeIds) => setForm((current) => ({ ...current, attendeeIds }))}
            emptyText="أضف الطلاب إلى المجلس أولاً لتحديد الحضور."
          />
          <MultiChoice label="القرارات والتوصيات" options={optionsFor(DECISIONS)} value={form.decisions} onChange={(decisions) => setForm((current) => ({ ...current, decisions }))} />
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
            <Button type="submit" disabled={saving} className="bg-[#27624d] hover:bg-[#1f503e]">{saving ? "جاري الحفظ..." : "حفظ الاجتماع"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function StudentCouncil({ readOnly = false, counselorId = null, counselor = null }) {
  const { user } = useAuth();
  const president = counselor || (counselorId ? null : user);
  const printing = useCouncilPrint();
  const [studentDirectory, setStudentDirectory] = useState([]);
  const [roster, setRoster] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingRoster, setSavingRoster] = useState(false);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [grade, setGrade] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const canEdit = !readOnly && user?.role === "counselor" && (!counselorId || counselorId === user.id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = counselorId ? { counselor_id: counselorId } : {};
      const [studentsResponse, rosterResponse, meetingsResponse] = await Promise.all([
        api.get("/records", { params: { ...params, type: "student_record" } }),
        api.get("/records", { params: { ...params, type: ROSTER_TYPE } }),
        api.get("/records", { params: { ...params, type: MEETING_TYPE } }),
      ]);
      const directory = studentsResponse.data
        .map((record) => ({
          student_id: record.data?.student_id,
          student_name: record.data?.student_name,
          grade: normalizeGrade(record.data?.grade),
        }))
        .filter((student) => student.student_id && student.student_name);
      const savedRoster = rosterResponse.data[0] || null;
      const studentIds = new Set(directory.map((student) => student.student_id));
      const rosterStudents = (savedRoster?.data?.members || []).filter((student) => studentIds.has(student.student_id));
      setStudentDirectory(directory);
      setRoster(savedRoster);
      setSelectedStudents(rosterStudents.map((student) => student.student_id));
      setMeetings(meetingsResponse.data.sort((left, right) => String(right.data.date).localeCompare(String(left.data.date))));
      return true;
    } catch (error) {
      toast.error(formatErr(error));
      return false;
    } finally {
      setLoading(false);
    }
  }, [counselorId]);

  useEffect(() => { load(); }, [load]);

  const saveRoster = async () => {
    setSavingRoster(true);
    try {
      const members = studentDirectory
        .filter((student) => selectedStudents.includes(student.student_id))
        .map(({ student_id, student_name, grade }) => ({ student_id, student_name, grade }));
      const data = { president_id: president?.id, president_name: president?.name || "", members };
      if (roster?.id) await api.put(`/records/${roster.id}`, { data });
      else {
        const { data: saved } = await api.post("/records", { type: ROSTER_TYPE, data });
        setRoster(saved);
      }
      if (await load()) toast.success("تم حفظ أعضاء المجلس");
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setSavingRoster(false);
    }
  };

  const saveMeeting = async (data, id) => {
    try {
      const meetingData = { ...data, president_id: president?.id, president_name: president?.name || "" };
      if (id) await api.put(`/records/${id}`, { data: meetingData });
      else await api.post("/records", { type: MEETING_TYPE, data: meetingData });
      if (await load()) toast.success(id ? "تم تحديث الاجتماع" : "تم حفظ الاجتماع");
    } catch (error) {
      toast.error(formatErr(error));
      throw error;
    }
  };

  const removeMeeting = async (meeting) => {
    if (!window.confirm("هل تريد حذف هذا الاجتماع؟")) return;
    try {
      await api.delete(`/records/${meeting.id}`);
      if (await load()) toast.success("تم حذف الاجتماع");
    } catch (error) {
      toast.error(formatErr(error));
    }
  };

  const grades = gradesForSchool(president?.school_key);
  const gradeStudents = studentDirectory.filter((student) => student.grade === grade && (!search.trim() || student.student_name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())));
  const savedMembers = (roster?.data?.members || []).filter((member) => studentDirectory.some((student) => student.student_id === member.student_id));
  const meetingMembers = [
    ...savedMembers,
    ...(editingMeeting?.data?.attendees || [])
      .filter((attendee) => !savedMembers.some((member) => member.student_id === attendee.student_id))
      .map((attendee) => ({ student_id: attendee.student_id, student_name: attendee.name, grade: attendee.grade })),
  ];
  const statusLabel = (status) => MEETING_STATUSES.find((option) => option.value === status)?.label || "مجدول";

  return (
    <div className="space-y-6" data-testid="student-council-page">
      <RecordApproval section="student_council" counselorId={counselorId} counselorGender={president?.gender} contentCount={meetings.length + (roster ? 1 : 0)} />
      <Button variant="outline" disabled={loading} onClick={() => printing.print()} data-testid="print-student-council"><Printer className="me-2 h-4 w-4" /> طباعة السجل والمحاضر A4</Button>
      <section className="relative overflow-hidden rounded-[1.75rem] bg-gradient-to-br from-[#173a50] via-[#1e5970] to-[#38859a] p-6 text-white shadow-[0_20px_50px_rgba(29,76,95,0.18)] sm:p-8">
        <div className="pointer-events-none absolute -left-12 -top-24 h-64 w-64 rounded-full border border-white/10 shadow-[0_0_0_28px_rgba(255,255,255,0.035),0_0_0_58px_rgba(255,255,255,0.025)]" />
        <div className="relative flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium text-[#ccecf1]">
              <GraduationCap className="h-4 w-4" /> المشاركة الطلابية
            </div>
            <h1 className="text-2xl font-bold sm:text-3xl" style={{ fontFamily: "IBM Plex Sans Arabic" }}>سجل المجلس الطلابي</h1>
            <p className="mt-2 max-w-xl text-sm leading-7 text-white/75">كوّن مجموعة المجلس من طلاب سجلاتك، ووثّق اجتماعاتهم ومبادراتهم.</p>
          </div>
          {canEdit && (
            <Button onClick={() => { setEditingMeeting(null); setMeetingDialogOpen(true); }} disabled={loading || !savedMembers.length} className="shrink-0 rounded-xl bg-[#d1edaa] text-[#183d30] hover:bg-[#c1e395]" data-testid="add-student-council-meeting">
              <Plus className="me-2 h-4 w-4" /> إضافة اجتماع
            </Button>
          )}
        </div>
        <div className="relative mt-7 grid grid-cols-2 gap-3 sm:max-w-md">
          <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
            <div className="flex items-center gap-2 text-xs text-white/70"><Users className="h-4 w-4" /> أعضاء المجلس</div>
            <div className="mt-2 text-3xl font-bold">{roster?.data?.members?.length || 0}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
            <div className="flex items-center gap-2 text-xs text-white/70"><ClipboardList className="h-4 w-4" /> الاجتماعات</div>
            <div className="mt-2 text-3xl font-bold">{meetings.length}</div>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-[#e5ebe6] bg-white shadow-[0_10px_30px_rgba(24,48,36,0.035)]">
        <div className="flex items-start gap-3 border-b border-[#edf0ed] p-5 sm:px-6">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#e9f2f5] text-[#2b6c82]"><Users className="h-5 w-5" /></span>
          <div>
            <h2 className="text-lg font-bold text-[#1e3429]">رئيس المجلس وأعضاؤه</h2>
            <p className="mt-1 text-sm text-[#718078]">{staffTitles(president?.gender).counselor} {president?.name ? `— ${president.name}` : ""} {president?.gender === "female" ? "هي رئيسة" : "هو رئيس"} المجلس، وتُختار المجموعة من سجل طلابك.</p>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-[#718078]">جاري تحميل الطلاب...</div>
        ) : (
          <div className="space-y-4 p-5 sm:p-6">
            <div className="flex flex-wrap items-center gap-3 rounded-xl bg-[#f2f7f8] p-3.5">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-white text-[#36788c] shadow-sm"><Crown className="h-5 w-5" /></span>
              <div>
                <div className="text-xs text-[#718078]">{president?.gender === "female" ? "رئيسة" : "رئيس"} المجلس — {staffTitles(president?.gender).counselor}</div>
                <div className="font-semibold text-[#263e46]">{president?.name || "حساب المرشد الحالي"}</div>
              </div>
            </div>
            {studentDirectory.length ? (
              <>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="font-semibold text-[#31443a]">أعضاء المجلس الطلابي</h3>
                    <p className="mt-1 text-xs text-[#78857d]">اختيار متعدد من الطلاب المسجلين في سجلات المرشد الحالي.</p>
                  </div>
                </div>
                {canEdit ? (
                  <>
                    <div className="space-y-2">
                      <Label>الصف</Label>
                      <Select value={grade} onValueChange={(value) => { setGrade(value); setSearch(""); setPickerOpen(true); }}>
                        <SelectTrigger aria-label="الصف"><SelectValue placeholder="اختر الصف أولاً" /></SelectTrigger>
                        <SelectContent>{grades.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {grade ? <StudentPicker students={gradeStudents} directory={studentDirectory} selectedIds={selectedStudents} onChange={setSelectedStudents} multiple
                      open={pickerOpen} setOpen={setPickerOpen} search={search} setSearch={setSearch} placeholder="بحث في طلاب الصف" emptyMessage="لا يوجد طلاب في هذا الصف."
                      testId="council-student" /> : <p className="text-sm text-[#718078]">اختر الصف لعرض طلابه.</p>}
                    {!grade && selectedStudents.length > 0 && <p>{studentDirectory.filter((student) => selectedStudents.includes(student.student_id)).map((student) => `${student.student_name} (${student.grade})`).join("، ")}</p>}
                  </>
                ) : <p>{savedMembers.map((student) => `${student.student_name} (${student.grade})`).join("، ") || "لا يوجد أعضاء مسجلون."}</p>}
                {canEdit && (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-sm text-[#718078]">تم اختيار {selectedStudents.length} من {studentDirectory.length} طالبًا</span>
                    <Button onClick={saveRoster} disabled={savingRoster} className="rounded-xl bg-[#286c80] hover:bg-[#20576a]" data-testid="save-student-council-members">
                      {savingRoster ? "جاري الحفظ..." : "حفظ أعضاء المجلس"}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-xl border border-dashed border-[#d7e4e5] bg-[#f8fbfb] px-5 py-8 text-center">
                <Users className="mx-auto h-8 w-8 text-[#7395a0]" />
                <p className="mt-3 font-semibold text-[#344b52]">لا توجد أسماء طلاب للاختيار</p>
                <p className="mt-1 text-sm text-[#71848a]">أضف الطلاب إلى «سجلات الطلاب» أولاً، ثم عد لاختيار أعضاء المجلس.</p>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="overflow-hidden rounded-2xl border border-[#e5ebe6] bg-white shadow-[0_10px_30px_rgba(24,48,36,0.035)]">
        <div className="flex items-start gap-3 border-b border-[#edf0ed] p-5 sm:px-6">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#f7f0df] text-[#a27b2c]"><CalendarDays className="h-5 w-5" /></span>
          <div>
            <h2 className="text-lg font-bold text-[#1e3429]">اجتماعات المجلس</h2>
            <p className="mt-1 text-sm text-[#718078]">سجل الموضوعات والحضور والقرارات بخيارات محددة.</p>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-[#718078]">جاري تحميل الاجتماعات...</div>
        ) : meetings.length ? (
          <div className="divide-y divide-[#edf0ed]">
            {meetings.map((meeting, index) => (
              <article key={meeting.id} className="grid gap-4 p-5 sm:grid-cols-[11rem_1fr] sm:px-6" data-testid={`student-council-meeting-${meeting.id}`}>
                <div>
                  <div className="flex items-center gap-2 font-semibold text-[#30453a]"><CalendarDays className="h-4 w-4 text-[#36788c]" />{formatDate(meeting.data.date)}</div>
                  {meeting.data.time && <div className="mt-2 flex items-center gap-1 text-xs text-[#839087]"><Clock3 className="h-3 w-3" />{meeting.data.time}</div>}
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-[#edf5f6] px-2.5 py-1 text-[11px] font-semibold text-[#327185]">اجتماع {meetings.length - index}</span>
                      <span className="rounded-full bg-[#f5f4ee] px-2.5 py-1 text-[11px] text-[#76683d]">{statusLabel(meeting.data.status)}</span>
                      {meeting.data.location && <span className="text-xs text-[#819087]">{meeting.data.location}</span>}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => printing.print(meeting)} data-testid={`print-council-meeting-${meeting.id}`}><Printer className="me-2 h-4 w-4" /> طباعة المحضر</Button>
                    {canEdit && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setEditingMeeting(meeting); setMeetingDialogOpen(true); }} className="h-8 rounded-lg text-[#557064]"><Pencil className="me-1.5 h-3.5 w-3.5" /> تعديل</Button>
                        <Button size="icon" variant="ghost" aria-label="حذف الاجتماع" onClick={() => removeMeeting(meeting)} className="h-8 w-8 text-[#a24d4d]"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 grid gap-3 lg:grid-cols-2">
                    <div className="rounded-xl bg-[#f5f9fa] p-3.5">
                      <div className="mb-2 text-xs font-bold text-[#53675b]">موضوعات الاجتماع</div>
                      <div className="flex flex-wrap gap-1.5">{(meeting.data.topics || []).length ? meeting.data.topics.map((topic) => <span key={topic} className="rounded-full bg-white px-2.5 py-1 text-xs text-[#50635a]">{topic}</span>) : <span className="text-xs text-[#87928b]">لم تحدد موضوعات</span>}</div>
                    </div>
                    <div className="rounded-xl bg-[#fbf8ef] p-3.5">
                      <div className="mb-2 text-xs font-bold text-[#876d38]">القرارات والتوصيات</div>
                      <div className="flex flex-wrap gap-1.5">{(meeting.data.decisions || []).length ? meeting.data.decisions.map((decision) => <span key={decision} className="rounded-full bg-white px-2.5 py-1 text-xs text-[#625840]">{decision}</span>) : <span className="text-xs text-[#87928b]">لم تحدد قرارات</span>}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-[#68776e]">الحضور</span>
                    {(meeting.data.attendees || []).length ? meeting.data.attendees.map((attendee) => (
                      <span key={attendee.student_id} className="inline-flex items-center gap-1 rounded-full border border-[#e7ece8] bg-white px-2.5 py-1 text-xs text-[#5a6c61]"><Check className="h-3 w-3 text-[#48805d]" />{attendee.name}</span>
                    )) : <span className="text-xs text-[#87928b]">لم يسجل حضور</span>}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mx-4 my-5 rounded-2xl border border-dashed border-[#e7e0cc] bg-[#fcfbf7] px-5 py-9 text-center sm:mx-5">
            <ClipboardList className="mx-auto h-8 w-8 text-[#a27b2c]" />
            <h3 className="mt-3 font-semibold text-[#453e2f]">لا توجد اجتماعات مسجلة</h3>
            <p className="mt-1 text-sm text-[#817b6e]">بعد اختيار أعضاء المجلس، أضف اجتماعًا لتوثيق نشاطهم.</p>
          </div>
        )}
      </section>

      <CouncilRecordPrint request={printing.request} onFinish={printing.finish} title="المجلس الطلابي" schoolKey={president?.school_key} schoolName={president?.school_name} counselorName={president?.name} counselorId={counselorId}
        counselorGender={president?.gender}
        members={[{ id: president?.id || "president", name: president?.name || "—", detail: `${president?.gender === "female" ? "رئيسة" : "رئيس"} المجلس — ${staffTitles(president?.gender).counselor}` }, ...(roster?.data?.members || []).map((member) => ({ id: member.student_id, name: member.student_name, detail: member.grade }))]} meetings={meetings} />
      <MeetingDialog open={meetingDialogOpen} onOpenChange={setMeetingDialogOpen} meeting={editingMeeting} members={meetingMembers} onSave={saveMeeting} />
    </div>
  );
}
